import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BellRing, Volume2, VolumeX, Play } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { playSoundLogo } from "./sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isPhone = () => window.matchMedia("(max-width: 767px)").matches;

export default function LaunchPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [muted, setMuted] = useState(true);
  const [phone] = useState(isPhone);
  const [started, setStarted] = useState(false);
  const videoRef = useRef(null);

  const toggleSound = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const tapToPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    setStarted(true);
    v.play().catch(() => {
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
  };

  useEffect(() => {
    if (sessionStorage.getItem("lp_popup_dismissed") === "1") return;
    const t = setTimeout(() => {
      setOpen(true);
      playSoundLogo(0.6);
    }, 22000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem("lp_popup_dismissed", "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/waitlist`, { email, source: "launch-popup" });
      setState("done");
      sessionStorage.setItem("lp_popup_dismissed", "1");
      window.dispatchEvent(new Event("lp:waitlist-joined"));
      setTimeout(() => setOpen(false), 2500);
    } catch {
      setState("idle");
      toast.error("Signal interrupted — check that email and retry.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="launch-popup"
        >
          <div
            className="absolute inset-0 bg-obsidian/85 backdrop-blur-md"
            onClick={close}
            data-testid="popup-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 24, clipPath: "circle(0% at 50% 50%)" }}
            animate={{ opacity: 1, scale: 1, y: 0, clipPath: "circle(120% at 50% 50%)" }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative glass-panel max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto p-5 sm:p-8 signal-glow !border-signal/30"
          >
            <button
              data-testid="popup-close-button"
              onClick={close}
              aria-label="Close launch notification popup"
              className="absolute top-2 right-2 flex items-center justify-center w-11 h-11 text-slate-400 hover:text-signal transition-colors duration-300"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-4 pr-10">
              <img src="/logo-symbol.webp" width={512} height={512} alt="Launchpad rocket symbol" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-signal">
                  Incoming Transmission
                </div>
                <h3 className="font-head font-extrabold tracking-tight text-lg text-white mt-1">
                  Get the launch signal.
                </h3>
              </div>
            </div>

            <div className="relative mt-5 overflow-hidden border border-white/10">
              <video
                ref={videoRef}
                className="w-full aspect-video object-cover"
                src="/teaser.mp4"
                poster="/teaser-poster.webp"
                preload={phone ? "none" : "metadata"}
                autoPlay={!phone}
                muted
                loop
                playsInline
                data-testid="popup-video"
              />
              <div className="absolute inset-0 scanlines pointer-events-none" />
              {phone && !started && (
                <button
                  data-testid="popup-tap-to-play"
                  onClick={tapToPlay}
                  aria-label="Play teaser with sound"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-obsidian/30"
                >
                  <span className="flex items-center justify-center w-16 h-16 rounded-full glass-panel !border-signal/50 signal-glow">
                    <Play size={24} className="text-signal translate-x-0.5" />
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">Tap to play · Sound on</span>
                </button>
              )}
              {(!phone || started) && (
              <button
                data-testid="popup-sound-toggle"
                onClick={toggleSound}
                className="absolute bottom-3 right-3 flex items-center gap-2 glass-panel px-3.5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-white hover:border-signal/60 transition-colors duration-300"
              >
                {muted ? <VolumeX size={13} className="text-signal" /> : <Volume2 size={13} className="text-signal" />}
                {muted ? "Sound On" : "Mute"}
              </button>
              )}
            </div>

            <p
              className="mt-3 font-mono text-[10px] tracking-[0.15em] uppercase text-signal/90 leading-relaxed"
              data-testid="popup-exclusive-label"
            >
              Launchpad TV Exclusive — C. Miller, AKA C-Murder: Only God Can Judge Me
            </p>

            {state === "done" ? (
              <div className="mt-6 text-center py-2" data-testid="popup-success">
                <BellRing size={22} className="text-signal mx-auto" />
                <p className="mt-3 font-head font-bold text-white">You're locked in.</p>
                <p className="mt-1 text-sm text-slate-400">
                  One transmission on 11.14.2026. No noise before then.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5" data-testid="popup-notify-form">
                <p className="text-sm text-slate-400 leading-relaxed">
                  One email when we go live — 11.14.2026. No noise before then.
                </p>
                <div className="mt-4 flex">
                  <input
                    data-testid="popup-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourchannel.tv"
                    className="flex-1 min-w-0 bg-white/5 border border-white/15 border-r-0 px-4 py-3 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors"
                  />
                  <button
                    data-testid="popup-notify-submit-button"
                    type="submit"
                    disabled={state === "sending"}
                    className="bg-signal text-obsidian font-bold font-mono text-[11px] tracking-[0.2em] uppercase px-5 hover:bg-white transition-colors duration-300 disabled:opacity-50"
                  >
                    {state === "sending" ? "..." : "Notify Me"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
